import React from 'react';
import { GroupDimension, PurchaseOrder } from '../types';
import { daysFrom, fmtDate } from '../utils/dateHelpers';
import { formatQty } from '../utils/formatHelpers';
import { nextPlanOf } from '../utils/statusHelpers';
import './PoDetailPanel.scss';

interface PoDetailPanelProps {
  po: PurchaseOrder;
  /** which field is already implied by the section this panel sits in */
  groupDimension: GroupDimension;
}

export const PoDetailPanel: React.FC<PoDetailPanelProps> = ({
  po,
  groupDimension,
}) => {
  const inTransit = [...po.inTransit].sort((a, b) =>
    a.dispatchDate.localeCompare(b.dispatchDate)
  );
  const schedule = [...po.deliverySchedule].sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  const nextPlan = nextPlanOf(po.deliverySchedule);

  // vendor is already fixed at the top level when grouped by client, and vice versa
  const contextLabel = groupDimension === 'client' ? po.vendor : po.city;

  return (
    <div className="po-detail">
      <div className="po-detail__row1">
        <span className="po-detail__num">
          <a href={po.url} target="_blank" rel="noopener noreferrer">
            {po.poNumber}
          </a>
        </span>
        <span>{contextLabel}</span>
        <span className="po-detail__figure">
          {formatQty(po.orderedQuantity, po.unit)} ordered
        </span>
        <span className="po-detail__figure">
          {formatQty(po.remainingQuantity, po.unit)} pending
        </span>
      </div>

      <div className="po-detail__schedule-group">
        <div className="po-detail__sched">
          <div className="po-detail__sched-title po-detail__sched-title--transit">
            🚚 In transit
          </div>
          {inTransit.length === 0 ? (
            <div className="po-detail__sched-empty">
              Nothing currently in transit
            </div>
          ) : (
            <>
              <div className="po-detail__sched-hd">
                <span>Dispatched on</span>
                <span>Qty</span>
              </div>
              {inTransit.map((entry, i) => (
                <div
                  className="po-detail__sched-row"
                  key={`${entry.dispatchDate}-${i}`}
                >
                  <span>{entry.dispatchDate}</span>
                  <span>{formatQty(entry.qty, po.unit)}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="po-detail__sched">
          <div className="po-detail__sched-title">Planned delivery</div>
          {schedule.length === 0 ? (
            <div className="po-detail__sched-empty">No plan set yet</div>
          ) : (
            <>
              <div className="po-detail__sched-hd">
                <span>Vendor Planned</span>
                <span>Client Expected</span>
                <span>Qty</span>
              </div>
              {schedule.map((entry, i) => {
                const isPast = daysFrom(entry.date) < 0;
                const isNext = entry.date === nextPlan;
                const rowClass = [
                  'po-detail__sched-row',
                  isPast && !isNext ? 'is-past' : '',
                  isNext ? 'is-next' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <div className={rowClass} key={`${entry.date}-${i}`}>
                    <span>{entry.date}</span>
                    <span>{entry.client_date}</span>
                    <span>{formatQty(entry.qty, po.unit)}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {po.latestComment && (
        <div className="po-detail__comment">{po.latestComment}</div>
      )}
    </div>
  );
};
